import { sortBy } from 'lodash';

import React from 'react';

import SessionGraphs, {
  GraphBlueprint,
  GraphOptions,
  GraphSeries,
  GraphPoint,
  GraphPlotband,
  GraphInstanceProps
} from '../index';
import { FormatDate } from '../../../../libs/time';
import { IEpxHyper, IEpxHyperSettings } from '../../../../libs/apps/epxhyper';
import { CHART } from '../../../../config/colors';

const EpxHyperGraphs: React.FC<GraphInstanceProps> = (props): JSX.Element => {
  const graph: GraphBlueprint<IEpxHyper, IEpxHyperSettings> = {
    name: 'Blood Pressure Graph',
    config: {
      chart: {
        type: 'line'
      },
      xAxis: {
        type: 'category',
        tickmarkPlacement: 'on',
        title: {
          text: null
        }
      },
      yAxis: {
        title: {
          text: 'Blood Pressure (mmHg)'
        }
      },
      responsive: {}
    },
    draw: (sessions, currentOptions, settings) => {
      const options: GraphOptions = {
        series: []
      };
      const systolicSeries: GraphSeries = {
        type: 'line',
        name: 'Systolic BP',
        connectNulls: true
      };
      const diastolicSeries: GraphSeries = {
        type: 'line',
        name: 'Diastolic BP',
        connectNulls: true
      };
      const data: GraphPoint[][] = [[], []];
      sessions = sortBy(sessions, (session) => session.time);
      for (const [i, session] of sessions.entries()) {
        const time = session.respondedAt || session.time;
        let { upper: systolic, lower: diastolic } = session;
        let color: string = null;
        // Do sanity check
        if (!(0 <= systolic && systolic <= 2000)) {
          systolic = null;
        } else if (systolic > 180 || systolic < 90) {
          color = CHART.DANGER;
        } else if (systolic > 140) {
          color = CHART.WARNING;
        } else {
          color = CHART.NORMAL;
        }
        data[0].push({
          x: i,
          y: systolic,
          name: FormatDate(time, 'MM/DD'),
          marker: {
            fillColor: color,
            lineColor: color,
            lineWidth: 2
          }
        });
        // Do sanity check
        if (!(0 <= diastolic && diastolic <= 2000)) {
          diastolic = null;
        } else if (diastolic > 110 || diastolic < 60) {
          color = CHART.DANGER;
        } else if (diastolic > 90) {
          color = CHART.WARNING;
        } else {
          color = CHART.NORMAL;
        }
        data[1].push({
          x: i,
          y: diastolic,
          name: FormatDate(time, 'MM/DD'),
          marker: {
            fillColor: color,
            lineColor: color,
            lineWidth: 2,
            radius: 7
          }
        });
      }
      systolicSeries.data = data[0];
      diastolicSeries.data = data[1];
      options.series = [systolicSeries, diastolicSeries];
      const plotBands: GraphPlotband[] = [];
      if (settings?.thresholds?.red?.upper?.min >= 0) {
        plotBands.push({
          from: 0,
          to: settings.thresholds?.red.upper.min,
          color: CHART.PLOT_BAND_DANGER
        });
      }
      if (settings?.thresholds?.red?.upper?.max >= 0) {
        plotBands.push({
          from: settings.thresholds?.red.upper.max,
          to: 10000,
          color: CHART.PLOT_BAND_DANGER
        });
      }
      options.yAxis = {
        ...currentOptions.yAxis,
        plotBands: plotBands
      };
      return options;
    }
  };
  return (
    <SessionGraphs<IEpxHyper>
      url="/data/data_by_range/"
      graphs={[graph]}
      {...props}
    />
  );
};

export default EpxHyperGraphs;
